import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {memo} from "react";
import {memoize} from "underscore";

import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";

interface BannerMessageType {
    severity: BannerSeverity;
    message: string;
}

// These are all the message keys that can be passed as a URL param. Note that
// these don't include message keys from linking an email, as that happens
// in a mutation with the banner being rendered in connect-email.jsx
type MessageKey =
    | "email_already_linked_to_your_account"
    | "email_already_linked_to_another_account"
    | "successfully_linked_email_to_account"
    | "you_can_now_login_with_apple"
    | "you_can_now_login_with_google"
    | "you_can_now_login_with_facebook"
    | "successfully_disconnected_cb_account"
    | "successfully_disconnected_apple_account"
    | "successfully_disconnected_google_account"
    | "successfully_disconnected_facebook_account"
    | "successfully_disconnected_microsoft_account"
    | "cannot_disconnect_last_account"
    | "unable_to_disconnect_account"
    | "clever_reauth_unsuccessful"
    | "didnt_login_successfully"
    | "didnt_login_successfully_v2"
    | "account_already_linked"
    | "already_linked_an_apple_account"
    | "already_linked_a_google_account"
    | "already_linked_a_facebook_account"
    | "no_email_associated_with_facebook_account"
    | "successfully_reauthenticated"
    | "reauth_required"
    | "link_account_error";

type BannerSeverity = "success" | "error";

const getMessageKeyMapping: () => Partial<
    Record<MessageKey, BannerMessageType>
> = memoize(
    (): Partial<Record<MessageKey, BannerMessageType>> => ({
        you_can_now_login_with_apple: {
            severity: "success",
            message: t`You can now log in with your Apple account.`,
        },
        you_can_now_login_with_google: {
            severity: "success",
            message: t`You can now log in with your Google account.`,
        },
        you_can_now_login_with_facebook: {
            severity: "success",
            message: t`You can now log in with your Facebook account.`,
        },
        successfully_disconnected_cb_account: {
            severity: "success",
            message: t`Your CollegeBoard.org account is now disconnected.`,
        },
        successfully_disconnected_apple_account: {
            severity: "success",
            message: t`Your Apple account is now disconnected.`,
        },
        successfully_disconnected_google_account: {
            severity: "success",
            message: t`Your Google account is now disconnected.`,
        },
        successfully_disconnected_facebook_account: {
            severity: "success",
            message: t`Your Facebook account is now disconnected.`,
        },
        successfully_disconnected_microsoft_account: {
            severity: "success",
            message: t`Your Microsoft account is now disconnected.`,
        },
        cannot_disconnect_last_account: {
            severity: "error",
            message: t`You cannot disconnect this account since it would leave you unable to log in again.`,
        },
        unable_to_disconnect_account: {
            severity: "error",
            message: t`Unable to disconnect account.`,
        },
        clever_reauth_unsuccessful: {
            severity: "error",
            message: t`Sorry, reauthentication through Clever has failed.`,
        },
        didnt_login_successfully: {
            severity: "error",
            message: t`Sorry, you did not log into Google or Facebook successfully.`,
        },
        // NOTE(jeremy): We require a "V2" version because we don't to start
        // showing "Apple" in the message until translations are complete and we
        // are live with Sign in with Apple.Once that occurs, we can remove the
        // original version and remove the _V2 suffix.
        didnt_login_successfully_v2: {
            severity: "error",
            message: t`Sorry, you did not log into Apple, Google, or Facebook successfully.`,
        },
        account_already_linked: {
            severity: "error",
            message: t`Sorry, another Khan Academy account is already using that login or its email address. You need to disconnect that login from your other Khan Academy account first.`,
        },
        already_linked_an_apple_account: {
            severity: "error",
            message: t`You already connected an Apple account.`,
        },
        already_linked_a_google_account: {
            severity: "error",
            message: t`You already connected a Google account.`,
        },
        already_linked_a_facebook_account: {
            severity: "error",
            message: t`You already connected a Facebook account.`,
        },
        no_email_associated_with_facebook_account: {
            severity: "error",
            message: t`We couldn't connect your Facebook account because we didn't receive your email address from Facebook.`,
        },
        successfully_reauthenticated: {
            severity: "success",
            message: t`Successfully verified your account. You may now change your settings.`,
        },
        reauth_required: {
            severity: "error",
            message: t`Reauthentication required to perform this action. Please try again.`,
        },
        link_account_error: {
            severity: "error",
            message: t`Oops! Something went wrong. Please try again.`,
        },
    }),
);

const MESSAGE_KEY_MAPPING_WITH_PARAM: Partial<
    Record<MessageKey, (arg1: string) => BannerMessageType>
> = {
    email_already_linked_to_your_account: (email: string) =>
        ({
            severity: "error",
            message: t`The email address ${email} is already linked to your Khan Academy account.`,
        }) as BannerMessageType,
    email_already_linked_to_another_account: (email: string) =>
        ({
            severity: "error",
            message: t`Sorry, ${email} is already linked to another Khan Academy account. Please log into it and unlink the email address or delete the account in order to link the email address to this account.`,
        }) as BannerMessageType,
    successfully_linked_email_to_account: (email: string) =>
        ({
            severity: "success",
            message: t`Successfully linked ${email} to your Khan Academy account.`,
        }) as BannerMessageType,
};

const getBannerMessageFromMessageKey = (
    messageKey?: string | null,
    email?: string,
): BannerMessageType | null | undefined => {
    if (!messageKey) {
        return null;
    }

    const MESSAGE_KEY_MAPPING = getMessageKeyMapping();
    if (messageKey in MESSAGE_KEY_MAPPING) {
        return MESSAGE_KEY_MAPPING[messageKey];
    }

    if (messageKey in MESSAGE_KEY_MAPPING_WITH_PARAM) {
        return MESSAGE_KEY_MAPPING_WITH_PARAM[messageKey](email);
    }
};

const MessageBanner = ({
    messageKey,
    email,
}: {
    messageKey: string | null;
    email?: string;
}) => {
    const bannerMessage = getBannerMessageFromMessageKey(messageKey, email);
    if (bannerMessage) {
        return (
            <View
                style={[
                    styles.banner,
                    bannerMessage.severity === "error" && styles.error,
                    bannerMessage.severity === "success" && styles.success,
                ]}
            >
                <View style={{paddingInlineEnd: sizing.size_160}}>
                    <LabelLarge>{bannerMessage.message}</LabelLarge>
                </View>
            </View>
        );
    }
    return null;
};

const styles = StyleSheet.create({
    banner: {
        flexDirection: "row",
        padding: sizing.size_160,
        marginBlockEnd: sizing.size_160,
        borderRadius: 6,
    },
    error: {
        backgroundColor: semanticColor.status.critical.background,
    },
    success: {
        backgroundColor: semanticColor.status.success.background,
    },
});

export default memo(MessageBanner);
